import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GameShell from '../../shared/ui/GameShell';
import LoadingScreen from '../../shared/ui/LoadingScreen';
import ErrorScreen from '../../shared/ui/ErrorScreen';
import FinishedDaily from '../../shared/ui/FinishedDaily';
import { finish as recordOutcome, isFinished } from '../../shared/dailySession';
import { useDailySnapshot, usePersistedState } from '../../shared/useDailySession';
import DailyDoneOverlay from '../../shared/ui/DailyDoneOverlay';
import { loadTodaysPuzzle, formatDateLabel } from '../../shared/daily';
import {
  isAlreadyRevealed,
  matchAnswer,
  normalizePuzzle,
  totalPossible,
  type TopAnswerLevel,
  type TopAnswerPuzzle,
} from './types';
import './topAnswer.css';

export default function TopAnswerGame() {
  const [data, setData] = useState<{
    puzzle: TopAnswerPuzzle;
    dateKey: string;
    isToday: boolean;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadTodaysPuzzle<TopAnswerLevel>('topAnswer')
      .then((res) => {
        if (cancelled) return;
        if (!res) {
          setLoadError('No daily puzzle published for Top Answer yet.');
          return;
        }
        const puzzle = normalizePuzzle(res.data);
        if (!puzzle) {
          setLoadError("Today's Top Answer puzzle is malformed.");
          return;
        }
        setData({ puzzle, dateKey: res.dateKey, isToday: res.isToday });
      })
      .catch((e) => !cancelled && setLoadError(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loadError) return <ErrorScreen message={loadError} gameId="topAnswer" />;
  if (!data) {
    return (
      <GameShell gameId="topAnswer">
        <LoadingScreen label="Fetching today's puzzle…" />
      </GameShell>
    );
  }

  // A daily is one attempt. Once the round has resolved, show the result
  // instead of mounting a fresh board.
  if (isFinished('topAnswer', data.dateKey)) {
    return (
      <FinishedDaily gameId="topAnswer" dateKey={data.dateKey} isToday={data.isToday} />
    );
  }

  return (
    <TopAnswerPlay
      key={data.dateKey}
      puzzle={data.puzzle}
      dateKey={data.dateKey}
      isToday={data.isToday}
    />
  );
}

function TopAnswerPlay({
  puzzle,
  dateKey,
  isToday,
}: {
  puzzle: TopAnswerPuzzle;
  dateKey: string;
  isToday: boolean;
}) {
  const { restored, save } = useDailySnapshot<{
    revealed: number[];
    strikes: number;
  }>('topAnswer', dateKey);

  const [revealed, setRevealed] = useState<Set<number>>(
    () => new Set(restored?.revealed ?? []),
  );
  const [strikes, setStrikes] = useState(restored?.strikes ?? 0);

  const [input, setInput] = useState('');
  const [feedback, setFeedback] = useState('');
  const [shake, setShake] = useState(false);
  const [outcome, setOutcome] = useState<'won' | 'lost' | null>(null);

  usePersistedState(save, { revealed: [...revealed], strikes }, outcome === null);
  const [lastRevealed, setLastRevealed] = useState<number | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const shakeTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => clearTimeout(shakeTimer.current), []);

  const settledRef = useRef(false);
  useEffect(() => {
    if (!outcome || settledRef.current) return;
    settledRef.current = true;
    if (outcome === 'won') {
      recordOutcome('topAnswer', dateKey, 'won', {
        subtitle: `Found all ${puzzle.answers.length} answers.`,
      });
    } else {
      recordOutcome('topAnswer', dateKey, 'lost', {
        subtitle: `Found ${revealed.size} of ${puzzle.answers.length}.`,
        answer: puzzle.answers[0]?.canonical,
      });
    }
  }, [outcome, dateKey, puzzle, revealed]);

  const isGameOver = outcome !== null;
  const possible = useMemo(() => totalPossible(puzzle), [puzzle]);
  const score = useMemo(
    () => [...revealed].reduce((sum, i) => sum + puzzle.answers[i].percentage, 0),
    [revealed, puzzle],
  );

  const registerStrike = useCallback(
    (message: string) => {
      setFeedback(message);
      setStrikes((s) => {
        const next = s + 1;
        if (next >= puzzle.maxStrikes) setOutcome('lost');
        return next;
      });
    },
    [puzzle.maxStrikes],
  );

  const submit = useCallback(() => {
    if (isGameOver) return;
    const trimmed = input.trim();
    if (!trimmed) return;

    // Re-guessing a found answer is free — it must not burn a strike.
    if (isAlreadyRevealed(trimmed, puzzle.answers, revealed)) {
      setFeedback('Already found!');
      setInput('');
      return;
    }

    const hit = matchAnswer(trimmed, puzzle.answers, revealed);
    if (hit !== null) {
      const next = new Set(revealed).add(hit);
      setRevealed(next);
      setLastRevealed(hit);
      setInput('');
      setFeedback('');
      if (next.size === puzzle.answers.length) setOutcome('won');
      return;
    }

    setShake(true);
    clearTimeout(shakeTimer.current);
    shakeTimer.current = window.setTimeout(() => setShake(false), 400);
    setInput('');
    registerStrike('Not a top answer');
  }, [isGameOver, input, puzzle, revealed, registerStrike]);

  /** Skipping costs a strike, with the same end-of-round handling as a miss. */
  const skip = useCallback(() => {
    if (isGameOver) return;
    setInput('');
    registerStrike('Skipped — strike added');
  }, [isGameOver, registerStrike]);

  return (
    <GameShell
      gameId="topAnswer"
      subtitle={`${formatDateLabel(dateKey, isToday)} • ${revealed.size}/${
        puzzle.answers.length
      } found`}
    >
      <div className="ta-board">
        <div className="ta-question-card">
          <div className="ta-category">{puzzle.category.toUpperCase()}</div>
          <div className="ta-question">{puzzle.question}</div>
        </div>

        <div className="ta-scorebar">
          <div className="ta-score">
            <span className="ta-score-value">{score}</span>
            <span className="ta-score-total">/ {possible}</span>
          </div>
          <div className="ta-strikes">
            <span className="ta-strikes-label">STRIKES</span>
            <div className="ta-strikes-dots">
              {Array.from({ length: puzzle.maxStrikes }).map((_, i) => (
                <span
                  key={i}
                  className={'ta-strike' + (i < strikes ? ' is-spent' : '')}
                  aria-hidden
                >
                  {i < strikes ? '✕' : ''}
                </span>
              ))}
            </div>
          </div>
        </div>

        <ol className="ta-list">
          {puzzle.answers.map((answer, i) => {
            const shown = revealed.has(i) || isGameOver;
            return (
              <li
                key={answer.canonical}
                className={
                  'ta-slot' +
                  (shown ? ' is-revealed' : '') +
                  (i === lastRevealed ? ' is-fresh' : '') +
                  (!revealed.has(i) && isGameOver ? ' is-missed' : '')
                }
              >
                <span className="ta-rank">{i + 1}</span>
                <span className="ta-answer">
                  {shown ? answer.canonical : '• • • • •'}
                </span>
                <span className="ta-pct">{shown ? `${answer.percentage}%` : ''}</span>
              </li>
            );
          })}
        </ol>

        {feedback && <div className="ta-feedback">{feedback}</div>}

        <form
          className={'ta-input-row' + (shake ? ' is-shake' : '')}
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <input
            ref={inputRef}
            className="ta-input"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              if (feedback) setFeedback('');
            }}
            placeholder="Type an answer…"
            disabled={isGameOver}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            aria-label="Your answer"
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isGameOver || !input.trim()}
          >
            Guess
          </button>
        </form>

        <button
          type="button"
          className="btn btn-ghost ta-skip"
          onClick={skip}
          disabled={isGameOver}
        >
          Skip (costs a strike)
        </button>

        {outcome === 'won' && (
          <DailyDoneOverlay
            dateKey={dateKey}
            isToday={isToday}
            subtitle={`Found all ${puzzle.answers.length} answers with ${strikes} strike${
              strikes === 1 ? '' : 's'
            }.`}
          />
        )}

        {outcome === 'lost' && (
          <div className="ta-overlay">
            <div className="ta-overlay-card">
              <div className="ta-overlay-emoji">😬</div>
              <div className="ta-overlay-title">Out of strikes</div>
              <div className="ta-overlay-sub">
                You found {revealed.size} of {puzzle.answers.length} — scoring {score} of{' '}
                {possible}. The rest are revealed above.
              </div>
            </div>
          </div>
        )}
      </div>
    </GameShell>
  );
}
